import { Instance, types } from 'mobx-state-tree';
import getAppRoot from '../../utils/getAppRoot';
import { ITableRow } from '.';
import { getErrors, detectBestDateFormat, type DateFormatFamily } from '../../../../components/orders/UploadStops/helpers/csvTableHelpers';
import { AddOrderDto } from '../../models/RootStore.base';
import {
  HeaderColumnKeyEnum,
  HeaderDataTypeEnum,
  HeaderNodeTypeEnum,
} from '../../../../components/orders/UploadStops/types/columnHeader';
import { CellWithDependencyMeta, CSVUploadSteps } from '../../../../components/orders/UploadStops/types/CSVTypes';
import { DateFormatPreference } from '../../models/DateFormatPreferenceEnum';

export const ColumnHeader = types.model({
  columnKey: types.identifier,
  label: types.string,
  matchKeys: types.array(types.string),
  matchedIndex: types.optional(types.refinement(types.number, value => value >= -1), -1),
  nodePath: types.frozen<keyof AddOrderDto>(),
  nodeType: types.enumeration<HeaderNodeTypeEnum>('HeaderNodeTypeEnum', [...Object.values(HeaderNodeTypeEnum)]),
  dataType: types.enumeration<HeaderDataTypeEnum>('HeaderDataTypeEnum', [...Object.values(HeaderDataTypeEnum)]),
  order: types.number,
  dependsOn: types.maybe(types.union(types.undefined, types.enumeration<HeaderColumnKeyEnum>('HeaderColumnKeyEnum', [...Object.values(HeaderColumnKeyEnum)]))),
  customErrorMessage: types.maybe(types.string),
  isLocationHeader: types.boolean,
}).views((self) => ({
  get required(): boolean {
    if (self.nodePath === 'locations') {
      return this.isAddressAndLatLongUnmapped || (this.isLatLongAndDependencyUnmapped && this.isLatLongAndAddressUnmapped);
    }
    return false;
  },
  get isAddressAndLatLongUnmapped(): boolean {
    const { mappedLocationFields } = getAppRoot(self).upload;

    return self.columnKey === HeaderColumnKeyEnum.ADDRESS
      && !mappedLocationFields.includes(HeaderColumnKeyEnum.LATITUDE) && !mappedLocationFields.includes(HeaderColumnKeyEnum.LONGITUDE);
  },
  get isLatLongAndAddressUnmapped(): boolean {
    const { mappedLocationFields } = getAppRoot(self).upload;

    return (self.columnKey === HeaderColumnKeyEnum.LATITUDE
      || self.columnKey === HeaderColumnKeyEnum.LONGITUDE)
      && !mappedLocationFields.includes(HeaderColumnKeyEnum.ADDRESS);
  },
  get isLatLongAndDependencyUnmapped(): boolean {
    const { mappedLocationFields } = getAppRoot(self).upload;

    return (self.columnKey === HeaderColumnKeyEnum.LATITUDE
      || self.columnKey === HeaderColumnKeyEnum.LONGITUDE)
      && (!!self.dependsOn && mappedLocationFields.includes(self.dependsOn));
  },
  get mappingCells(): CellWithDependencyMeta[] {
    if (this.matched) {
      const { tableData } = getAppRoot(self).upload;

      return tableData.map((row: ITableRow) => ({
        cell: row.cells[self.matchedIndex],
        ...(this.dependentHeader?.matchedIndex ? { dependsOn: row.cells[this.dependentHeader.matchedIndex] } : {}),
      }));
    }
    return [];
  },
  get verifyingCells(): CellWithDependencyMeta[] {
    if (this.matched) {
      const { editedTableData } = getAppRoot(self).upload;

      return editedTableData.map((row: ITableRow) => ({
        cell: row.cells[self.matchedIndex],
        ...(this.dependentHeader?.matchedIndex ? { dependsOn: row.cells[this.dependentHeader.matchedIndex] } : {}),
      }));
    }
    return [];
  },
  get mappingErrors(): any {
    const { tableData, mappedLocationMatchedHeaders } = getAppRoot(self).upload;
    return this.matched
      ? getErrors(this.mappingCells, self, getAppRoot(self).ui.geolocation?.countryCode, tableData, mappedLocationMatchedHeaders)
      : {};
  },
  get verifyingErrors(): any {
    const { editedTableData, mappedLocationMatchedHeaders } = getAppRoot(self).upload;
    return this.matched
      ? getErrors(this.verifyingCells, self, getAppRoot(self).ui.geolocation?.countryCode, editedTableData, mappedLocationMatchedHeaders)
      : {};
  },
  get validType(): boolean {
    if (this.matched) {
      const { showImportDialog } = getAppRoot(self).ui;
      const { editedTableData } = getAppRoot(self).upload;

      const errors = showImportDialog?.step === CSVUploadSteps.CONFIRM && editedTableData.length > 0
        ? this.verifyingErrors
        : this.mappingErrors;

      return errors && Object.keys(errors).length === 0;
    }
    return true;
  },
  get dependentHeader(): IColumnHeader | undefined {
    return getAppRoot(self).upload.matchedHeaders.find((header: IColumnHeader) => header.dependsOn === self.columnKey);
  },
  get hasMatchedDependency(): boolean {
    return this.dependentHeader?.dependsOn === self.columnKey;
  },
  get preEditErroredRows(): string[] {
    return Object.keys(this.mappingErrors);
  },
  get erroredRows(): { [key: string]: string } {
    const { showImportDialog } = getAppRoot(self).ui;
    const { editedTableData } = getAppRoot(self).upload;

    return (showImportDialog?.step === CSVUploadSteps.CONFIRM || !showImportDialog?.visible) && editedTableData.length > 0 ? this.verifyingErrors : this.mappingErrors;
  },
  get errorCount(): number {
    return Object.keys(this.erroredRows).length;
  },
  get matched(): boolean {
    return self.matchedIndex !== -1;
  },
  get erroredRowIdxs(): string[] {
    return Object.keys(this.erroredRows);
  },
  /**
   * Detects the most appropriate date format family for this column by analyzing all date values.
   * Respects organization's date format preference setting if it's not set to AUTO.
   * For ambiguous formats (DD-first vs MM-first), picks the family with the most valid dates.
   * Only applies to date-type columns.
   *
   * @returns The detected date format family ('MM-first' or 'DD-first'), or null if not a date column or no clear family
   */
  get detectedDateFormatFamily(): DateFormatFamily | null {
    // Only detect format for date columns that are matched
    if (!this.matched || self.dataType !== HeaderDataTypeEnum.DATE) {
      return null;
    }

    // Get all non-empty date values from the column
    const dateValues = this.mappingCells
      .map((cellMeta) => cellMeta.cell?.value)
      .filter((value): value is string => !!value && typeof value === 'string' && value.trim().length > 0);

    if (dateValues.length === 0) {
      return null;
    }

    // Use organization's date format preference if available
    const orgDateFormatPreference = getAppRoot(self).organization.settings?.dateFormatPreference;
    return detectBestDateFormat(dateValues, orgDateFormatPreference as DateFormatPreference);
  },
})).actions((self) => ({
  mapToIndex(index: number) {
    const { setEditedTableData } = getAppRoot(self).upload;
    self.matchedIndex = index;
    // Reset data in verifying step
    setEditedTableData([]);
  },
}));

export interface IColumnHeader extends Instance<typeof ColumnHeader> {
}
